import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, type Variants } from 'motion/react';
import { scanInterpretationData } from '@/mocks/scanInterpretation';
import { recordScan } from '@/mocks/scanHistory';
import { createStudySession } from '@/mocks/studySessions';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';
import SaveSessionModal from '../components/SaveSessionModal';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.15 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 28 } },
};

export default function ScanResult() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const textPrimary = isDark ? 'text-white' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-white/40' : 'text-neutral-400';
  const textMuted = isDark ? 'text-white/30' : 'text-neutral-500';
  const textFaint = isDark ? 'text-white/15' : 'text-neutral-300';

  const pageBg = isDark ? 'bg-[#0F0F10]' : 'bg-white';
  const cardBg = isDark ? 'bg-[#1C1D21]' : 'bg-neutral-50';
  const cardBorder = isDark ? 'border-white/[0.05]' : 'border-neutral-100';
  const headerBg = isDark ? 'bg-[#0F0F10]/90' : 'bg-white/90';
  const headerBorder = isDark ? 'border-white/[0.05]' : 'border-neutral-100';
  const iconBg = isDark ? 'bg-white/[0.04]' : 'bg-neutral-100';
  const iconColor = isDark ? 'text-[#8E8E93]' : 'text-neutral-500';
  const badgeBg = isDark ? 'bg-[#2A2A2E]' : 'bg-neutral-200';
  const badgeText = isDark ? 'text-[#A1A1A6]' : 'text-neutral-600';
  const preBg = isDark ? 'bg-[#0F0F10]' : 'bg-neutral-50';
  const codeBlockBorder = isDark ? 'border-white/[0.04]' : 'border-neutral-100';
  const topicTagBg = isDark ? 'bg-white/[0.04]' : 'bg-neutral-100';
  const topicTagBorder = isDark ? 'border-white/[0.04]' : 'border-neutral-200';

  const data = scanInterpretationData;
  const [saved, setSaved] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // Show save modal when arriving from generation flow
  useEffect(() => {
    const awaiting = sessionStorage.getItem('scan_awaiting_save') === 'true';
    if (awaiting) {
      setShowSaveModal(true);
    }
  }, []);

  const handleSaveSession = (customName: string) => {
    sessionStorage.removeItem('scan_awaiting_save');
    createStudySession({
      title: customName,
      subject: data.subject,
      topicName: data.overallSubject.topicsDetected[0],
      type: 'text',
      uploadType: 'Image',
    });
    recordScan({
      generationId: data.id,
      type: 'text',
      title: customName,
      subject: data.subject,
      uploadType: 'Image',
    });
    setSaved(true);
    setShowSaveToast(true);
    setShowSaveModal(false);
    setTimeout(() => setShowSaveToast(false), 2500);
  };

  const handleSkipSave = () => {
    sessionStorage.removeItem('scan_awaiting_save');
    setShowSaveModal(false);
  };

  const handleManualSave = () => {
    recordScan({
      generationId: data.id,
      type: 'text',
      title: data.title,
      subject: data.subject,
      uploadType: 'Image',
    });
    setSaved(true);
    setShowSaveToast(true);
    setTimeout(() => setShowSaveToast(false), 2200);
  };

  const handleBack = () => {
    navigate('/history');
  };

  /* ── Inline bold parser ── */
  function parseBold(text: string): React.ReactNode {
    const parts: React.ReactNode[] = [];
    const regex = /\*\*(.*?)\*\*/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</span>);
      }
      parts.push(
        <strong key={`b-${key++}`} className={`font-semibold ${isDark ? 'text-white' : 'text-neutral-900'}`}>
          {match[1]}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex)}</span>);
    }
    return parts.length > 0 ? parts : text;
  }

  /* ── Educational line parser ── */
  type LineType = 'goal' | 'def' | 'concept' | 'formula' | 'note' | 'science' | 'calc' | 'why' | 'text';
  interface ParsedLine { type: LineType; text: string; }

  function parseLineType(line: string): ParsedLine {
    const trimmed = line.trim();
    if (trimmed.startsWith('GOAL:')) return { type: 'goal', text: trimmed.slice(5).trim() };
    if (trimmed.startsWith('DEF:')) return { type: 'def', text: trimmed.slice(4).trim() };
    if (trimmed.startsWith('CONCEPT:')) return { type: 'concept', text: trimmed.slice(8).trim() };
    if (trimmed.startsWith('FORMULA:')) return { type: 'formula', text: trimmed.slice(8).trim() };
    if (trimmed.startsWith('NOTE:')) return { type: 'note', text: trimmed.slice(5).trim() };
    if (trimmed.startsWith('SCIENCE:')) return { type: 'science', text: trimmed.slice(8).trim() };
    if (trimmed.startsWith('CALC:')) return { type: 'calc', text: trimmed.slice(5).trim() };
    if (trimmed.startsWith('WHY:')) return { type: 'why', text: trimmed.slice(4).trim() };
    return { type: 'text', text: trimmed };
  }

  const sectionConfig: Record<
    Exclude<LineType, 'goal' | 'text'>,
    { emoji: string; label: string; bgDark: string; bgLight: string; borderDark: string; borderLight: string; textDark: string; textLight: string }
  > = {
    def: { emoji: '📘', label: 'Definition', bgDark: 'bg-blue-500/[0.04]', bgLight: 'bg-blue-50/80', borderDark: 'border-blue-500/10', borderLight: 'border-blue-100', textDark: 'text-blue-300/90', textLight: 'text-blue-800' },
    concept: { emoji: '🧠', label: 'Key Concept', bgDark: 'bg-violet-500/[0.04]', bgLight: 'bg-violet-50/80', borderDark: 'border-violet-500/10', borderLight: 'border-violet-100', textDark: 'text-violet-300/90', textLight: 'text-violet-800' },
    formula: { emoji: '⚡', label: 'Formula', bgDark: 'bg-white/[0.03]', bgLight: 'bg-neutral-100/80', borderDark: 'border-white/[0.06]', borderLight: 'border-neutral-200', textDark: 'text-neutral-300', textLight: 'text-neutral-800' },
    note: { emoji: '📌', label: 'Important Note', bgDark: 'bg-amber-500/[0.04]', bgLight: 'bg-amber-50/80', borderDark: 'border-amber-500/10', borderLight: 'border-amber-100', textDark: 'text-amber-300/90', textLight: 'text-amber-800' },
    science: { emoji: '🧪', label: 'Science Explanation', bgDark: 'bg-emerald-500/[0.04]', bgLight: 'bg-emerald-50/80', borderDark: 'border-emerald-500/10', borderLight: 'border-emerald-100', textDark: 'text-emerald-300/90', textLight: 'text-emerald-800' },
    calc: { emoji: '📐', label: 'Calculation', bgDark: 'bg-slate-500/[0.04]', bgLight: 'bg-slate-50/80', borderDark: 'border-slate-500/10', borderLight: 'border-slate-200', textDark: 'text-slate-300', textLight: 'text-slate-800' },
    why: { emoji: '💡', label: 'Why', bgDark: 'bg-sky-500/[0.04]', bgLight: 'bg-sky-50/80', borderDark: 'border-sky-500/10', borderLight: 'border-sky-100', textDark: 'text-sky-300/90', textLight: 'text-sky-800' },
  };

  const renderStepLine = (line: string, lineIdx: number, qIdx: number) => {
    const { type, text } = parseLineType(line);
    const key = `step-${qIdx}-${lineIdx}`;

    if (type === 'goal') {
      return (
        <div key={key} className="mb-2 mt-1">
          <p className={`text-[13px] font-semibold ${textPrimary} leading-relaxed`}>
            {parseBold(text)}
          </p>
        </div>
      );
    }

    if (type === 'text') {
      return (
        <p key={key} className={`text-[13px] leading-[1.75] ${textSecondary} my-1.5`}>
          {parseBold(text)}
        </p>
      );
    }

    const config = sectionConfig[type];
    const isFormula = type === 'formula';
    const isCalc = type === 'calc';

    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: lineIdx * 0.04, duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
        className={`my-2 rounded-xl border overflow-hidden ${isDark ? `${config.borderDark} ${config.bgDark}` : `${config.borderLight} ${config.bgLight}`}`}
      >
        <div className="flex items-center gap-2 px-4 pt-3 pb-1">
          <span className="text-sm">{config.emoji}</span>
          <span className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? config.textDark : config.textLight} opacity-60`}>
            {config.label}
          </span>
        </div>
        <div className={`px-4 ${isFormula || isCalc ? 'pb-3 pt-1 text-center' : 'pb-3 pt-0.5'}`}>
          <p className={`${isFormula || isCalc ? 'text-[15px] font-mono leading-relaxed text-neutral-800 dark:text-neutral-200' : `text-[13px] leading-[1.7] ${isDark ? config.textDark : config.textLight}`}`}>
            {parseBold(text)}
          </p>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className={`flex flex-col ${pageBg} transition-colors duration-500 relative`}
    >
      {/* Save Toast */}
      <AnimatePresence>
        {showSaveToast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="absolute top-0 left-0 right-0 z-50 flex justify-center pt-4"
          >
            <div className={`flex items-center gap-2 px-4 py-2.5 rounded-full ${isDark ? 'bg-neutral-800 border border-white/[0.08]' : 'bg-white border border-neutral-200'} shadow-lg`}>
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-500 text-sm" />
              </div>
              <span className={`text-xs font-medium ${textSecondary}`}>Saved to History</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className={`sticky top-0 z-30 ${headerBg} backdrop-blur-xl border-b ${headerBorder} transition-colors duration-500`}>
        <div className="flex items-center justify-between px-5 py-4">
          <Pressable
            onClick={handleBack}
            className={`w-9 h-9 flex items-center justify-center rounded-xl ${iconBg} transition-colors duration-500`}
            scale={0.88}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`ri-arrow-left-line ${iconColor} transition-colors duration-500`} />
            </div>
          </Pressable>
          <div className="text-center">
            <h1 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Scan Results</h1>
            <p className={`text-[11px] ${textSecondary} transition-colors duration-500`}>{data.subject}</p>
          </div>
          <Pressable
            onClick={handleManualSave}
            className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center transition-colors duration-500`}
            scale={0.88}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${saved ? 'ri-check-fill text-emerald-500' : `ri-bookmark-line ${iconColor}`} transition-colors duration-500`} />
            </div>
          </Pressable>
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex-1 px-5 pb-8 pt-4 space-y-5"
      >
        {/* Clean extracted question — the upload remains input-only. */}
        <motion.div variants={itemVariants} className={`rounded-2xl border ${cardBorder} ${cardBg} p-5 transition-colors duration-500`}>
          <div className={`mb-2 text-[10px] font-semibold uppercase tracking-[0.16em] ${textMuted}`}>Extracted question</div>
          <p className={`whitespace-pre-wrap text-lg leading-relaxed ${textPrimary}`}>
            {data.cleanQuestion || data.detectedQuestions[0]?.text || data.extractedText}
          </p>
        </motion.div>

        {/* Subject Detection */}
        <motion.div variants={itemVariants} className={`rounded-2xl border ${cardBorder} ${cardBg} p-5 transition-colors duration-500`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-focus-3-line ${iconColor} text-sm transition-colors duration-500`} />
            </div>
            <h3 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Detected Subject</h3>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl ${badgeBg} flex items-center justify-center`}>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-function-line text-sm text-white/60" />
              </div>
            </div>
            <div>
              <p className={`text-sm font-semibold ${textPrimary}`}>{data.overallSubject.name}</p>
              <p className={`text-[11px] ${textSecondary}`}>Confidence: {data.overallSubject.confidence}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.overallSubject.topicsDetected.map((topic) => (
              <span
                key={topic}
                className={`text-[11px] font-medium px-3 py-1.5 rounded-full border ${topicTagBg} ${topicTagBorder} ${textMuted} transition-colors duration-500`}
              >
                {topic}
              </span>
            ))}
          </div>
        </motion.div>

        {/* Extracted Text */}
        <motion.div variants={itemVariants} className={`rounded-2xl border ${cardBorder} ${cardBg} p-4 transition-colors duration-500`}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-text-snippet ${iconColor} text-sm transition-colors duration-500`} />
            </div>
            <h3 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Extracted Text</h3>
          </div>
          <pre className={`text-xs leading-relaxed whitespace-pre-wrap font-mono ${preBg} rounded-xl p-3.5 border ${codeBlockBorder} ${textMuted} transition-colors duration-500`}>
            {data.extractedText}
          </pre>
        </motion.div>

        {/* Detected Equations */}
        <motion.div variants={itemVariants} className={`rounded-2xl border ${cardBorder} ${cardBg} p-5 transition-colors duration-500`}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-functions ${iconColor} text-sm transition-colors duration-500`} />
            </div>
            <h3 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Detected Equations</h3>
            <span className={`text-[10px] ${badgeBg} ${badgeText} px-2 py-0.5 rounded-full`}>
              {data.detectedEquations.length} found
            </span>
          </div>
          <div className="space-y-2">
            {data.detectedEquations.map((eq, i) => (
              <motion.div
                key={i}
                className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl ${preBg} border ${codeBlockBorder} transition-colors duration-500`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08, duration: 0.3 }}
              >
                <div className={`w-6 h-6 rounded-md ${badgeBg} flex items-center justify-center flex-shrink-0`}>
                  <span className={`text-[10px] font-bold ${badgeText}`}>{i + 1}</span>
                </div>
                <span className={`text-xs font-mono ${textSecondary}`}>{eq}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Detected Questions */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-questionnaire-line ${iconColor} text-sm transition-colors duration-500`} />
            </div>
            <h3 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Detected Questions</h3>
            <span className={`text-[10px] ${badgeBg} ${badgeText} px-2 py-0.5 rounded-full`}>
              {data.detectedQuestions.length} found
            </span>
          </div>
          <div className="space-y-3">
            {data.detectedQuestions.map((q, i) => (
              <motion.div
                key={q.number}
                className={`rounded-2xl border ${cardBorder} ${cardBg} p-4 transition-colors duration-500`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-7 h-7 rounded-lg ${badgeBg} flex items-center justify-center flex-shrink-0`}>
                    <span className={`text-xs font-bold ${badgeText}`}>{q.number}</span>
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs font-mono ${textSecondary} mb-1`}>{q.text}</p>
                    <p className={`text-[11px] font-semibold ${textPrimary}`}>{q.questionType}</p>
                  </div>
                </div>
                <div className={`ml-10 p-3 rounded-xl ${preBg} border ${codeBlockBorder} transition-colors duration-500`}>
                  <p className={`text-[11px] font-semibold ${textSecondary} mb-1.5`}>What is being asked:</p>
                  <p className={`text-xs leading-relaxed ${textMuted}`}>{q.whatIsBeingAsked}</p>
                </div>
                <div className="ml-10 mt-2 flex flex-wrap gap-1.5">
                  {q.keyTerms.map((term) => (
                    <span
                      key={term}
                      className={`text-[10px] px-2 py-1 rounded-md border ${topicTagBg} ${topicTagBorder} ${textMuted}`}
                    >
                      {term}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Step-by-Step AI Solutions */}
        <motion.div variants={itemVariants}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-sparkling-line ${iconColor} text-sm transition-colors duration-500`} />
            </div>
            <h3 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Step-by-Step Solutions</h3>
            <span className={`text-[10px] ${badgeBg} ${badgeText} px-2 py-0.5 rounded-full`}>
              {data.explanation.steps.length} problems solved
            </span>
          </div>
          <div className="space-y-3">
            {data.explanation.steps.map((step, i) => (
              <motion.div
                key={step.number}
                className={`rounded-2xl border ${cardBorder} ${cardBg} overflow-hidden transition-colors duration-500`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              >
                {/* Problem Header */}
                <div className={`p-4 border-b ${isDark ? 'border-white/[0.03]' : 'border-neutral-100'}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg ${badgeBg} flex items-center justify-center flex-shrink-0`}>
                      <span className={`text-xs font-bold ${badgeText}`}>{step.number}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold ${textSecondary} mb-1`}>{step.title}</p>
                    </div>
                  </div>
                </div>

                {/* Step Content */}
                <div className="p-4 space-y-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-list-check-2 ${iconColor} text-xs`} />
                    </div>
                    <h4 className={`text-[11px] font-bold ${textPrimary} uppercase tracking-wider`}>Solution</h4>
                  </div>
                  {step.content.map((line, lineIdx) => renderStepLine(line, lineIdx, i))}

                  {/* Final Answer Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + step.content.length * 0.04 }}
                    className={`mt-4 rounded-2xl border p-4 text-center ${
                      isDark ? 'border-emerald-500/15 bg-emerald-500/[0.04]' : 'border-emerald-200/80 bg-emerald-50/80'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2 mb-1.5">
                      <span className="text-base">✅</span>
                      <span className={`text-[11px] font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'} uppercase tracking-wider`}>Final Answer</span>
                    </div>
                    <p className={`text-[24px] font-bold font-mono ${isDark ? 'text-emerald-300' : 'text-emerald-700'} leading-tight`}>
                      {step.result}
                    </p>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Suggested Next Steps */}
        <motion.div variants={itemVariants} className={`rounded-2xl border ${cardBorder} ${cardBg} p-5 transition-colors duration-500`}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-route-line ${iconColor} text-sm transition-colors duration-500`} />
            </div>
            <h3 className={`text-sm font-bold ${textPrimary} transition-colors duration-500`}>Suggested Next Steps</h3>
          </div>
          <div className="space-y-2.5">
            <Pressable
              onClick={() => navigate('/lesson/lesson-001')}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${isDark ? 'border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05]' : 'border-neutral-100 bg-neutral-50 hover:bg-neutral-100'} transition-colors duration-200`}
              scale={0.97}
            >
              <div className={`w-9 h-9 rounded-lg ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} flex items-center justify-center`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-2-line text-white/40 text-sm" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${textPrimary}`}>Learn This Topic</p>
                <p className={`text-[11px] ${textMuted}`}>Full structured lesson with practice</p>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-right-s-line ${textFaint} text-sm`} />
              </div>
            </Pressable>
            <Pressable
              onClick={() => navigate('/lesson/lesson-001')}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${isDark ? 'border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05]' : 'border-neutral-100 bg-neutral-50 hover:bg-neutral-100'} transition-colors duration-200`}
              scale={0.97}
            >
              <div className={`w-9 h-9 rounded-lg ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} flex items-center justify-center`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-2-line text-white/40 text-sm" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${textPrimary}`}>Full Lesson on {data.subject}</p>
                <p className={`text-[11px] ${textMuted}`}>Complete learning path with practice</p>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-right-s-line ${textFaint} text-sm`} />
              </div>
            </Pressable>
            <Pressable
              onClick={() => navigate('/ai-assistant')}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border ${isDark ? 'border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05]' : 'border-neutral-100 bg-neutral-50 hover:bg-neutral-100'} transition-colors duration-200`}
              scale={0.97}
            >
              <div className={`w-9 h-9 rounded-lg ${isDark ? 'bg-white/[0.06]' : 'bg-neutral-100'} flex items-center justify-center`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-bard-line text-white/40 text-sm" />
                </div>
              </div>
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${textPrimary}`}>Ask AI Anything</p>
                <p className={`text-[11px] ${textMuted}`}>Chat about this problem</p>
              </div>
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`ri-arrow-right-s-line ${textFaint} text-sm`} />
              </div>
            </Pressable>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div variants={itemVariants} className="space-y-3 pt-2">
          <Pressable
            onClick={() => navigate('/lesson/lesson-001')}
            className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2"
            scale={0.97}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-book-2-line text-sm" />
            </div>
            <span className="whitespace-nowrap">Watch Full Lesson</span>
          </Pressable>
          <Pressable
            onClick={() => navigate('/scan/start')}
            className={`w-full ${cardBg} border ${cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500 ${textPrimary}`}
            scale={0.97}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-scan-line ${iconColor} text-sm`} />
            </div>
            <span className="whitespace-nowrap">Scan Another</span>
          </Pressable>
          <Pressable
            onClick={() => navigate('/history')}
            className={`w-full ${cardBg} border ${cardBorder} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors duration-500 ${textPrimary}`}
            scale={0.97}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-history-line ${iconColor} text-sm`} />
            </div>
            <span className="whitespace-nowrap">View History</span>
          </Pressable>
        </motion.div>
      </motion.div>
      <SaveSessionModal
        open={showSaveModal}
        subject={data.subject}
        topicName={data.overallSubject.topicsDetected[0]}
        type="text"
        onSave={handleSaveSession}
        onSkip={handleSkipSave}
      />
    </motion.div>
  );
}